import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useStrings } from '@/i18n/Localization';
import type { StringKey } from '@/i18n/strings';
import { formatBytes } from '@/store/quota';
import { useConnectionStatus } from '@/transport/ConnectionStatusProvider';
import { useSession } from '@/transport/SessionProvider';
import styles from '@/screens/SettingsScreen.module.css';

/**
 * Settings.
 *
 * Section 14 lists profile, sessions, notifications, privacy, data/storage and
 * account controls. Sign-out and the storage report are implemented because
 * both are Phase 1 obligations — Section 14 requires the logout warning about
 * unsent work, and Section 05 requires reduced offline capability to be
 * reported. The other sections are named and marked unavailable rather than
 * omitted, so the shape of the screen is settled and each one has an obvious
 * home: profile and sessions land with P1-12 and the account-lifecycle work in
 * Phase 5, notifications with P1-13.
 */
const PENDING_SECTIONS: readonly { title: StringKey; hint: StringKey }[] = [
  { title: 'settings.profile', hint: 'settings.profileHint' },
  { title: 'settings.sessions', hint: 'settings.sessionsHint' },
  { title: 'settings.notifications', hint: 'settings.notificationsHint' },
  { title: 'settings.privacy', hint: 'settings.privacyHint' },
  { title: 'settings.account', hint: 'settings.accountHint' },
];

export function SettingsScreen() {
  const { t } = useStrings();
  const { account, signOut, unsentOperationCount } = useSession();
  const { storage } = useConnectionStatus();
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    void unsentOperationCount().then(setPendingCount);
  }, [unsentOperationCount]);

  const requestSignOut = () => {
    // Section 14: warn before removing local caches if unsent messages would be
    // lost, and offer to cancel so the pending work is preserved. Nothing is
    // revoked or cleared until the user has answered.
    if (pendingCount !== null && pendingCount > 0) {
      setConfirming(true);
      return;
    }
    void signOut();
  };

  return (
    <main className={styles.screen} aria-label={t('settings.title')}>
      <header className={styles.header}>
        <Link to="/" className={styles.back}>
          {t('nav.back')}
        </Link>
        <h1 className={styles.title}>{t('settings.title')}</h1>
      </header>

      <div className={styles.body}>
        {account ? (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle} dir="auto">
              {account.displayName}
            </h2>
            <p className={styles.sectionHint} dir="ltr">
              {account.phoneE164}
            </p>
          </section>
        ) : null}

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('settings.storage')}</h2>
          <p className={styles.sectionHint}>
            {storage.usedBytes !== null && storage.quotaBytes !== null
              ? t('settings.storageUsage', {
                  used: formatBytes(storage.usedBytes),
                  quota: formatBytes(storage.quotaBytes),
                })
              : t('settings.storageUnknown')}
          </p>
          {storage.capability !== 'full' ? (
            <p className={styles.warning}>
              {t(storage.capability === 'unavailable' ? 'storage.unavailable' : 'storage.reduced')}
            </p>
          ) : null}
        </section>

        {PENDING_SECTIONS.map((section) => (
          <section key={section.title} className={styles.section}>
            <h2 className={styles.sectionTitle}>{t(section.title)}</h2>
            <p className={styles.sectionHint}>{t(section.hint)}</p>
            <p className={styles.pending}>{t('settings.pending')}</p>
          </section>
        ))}

        <section className={styles.section}>
          <button type="button" className={styles.signOut} onClick={requestSignOut}>
            {t('settings.signOut')}
          </button>
        </section>
      </div>

      {confirming ? (
        <div className={styles.backdrop}>
          <div
            className={styles.dialog}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="signout-title"
            aria-describedby="signout-body"
          >
            <h2 id="signout-title" className={styles.sectionTitle}>
              {t('settings.signOutPendingTitle')}
            </h2>
            <p id="signout-body" className={styles.sectionHint}>
              {t('settings.signOutPendingBody', { count: pendingCount ?? 0 })}
            </p>
            <div className={styles.dialogActions}>
              <button
                type="button"
                className={styles.cancel}
                onClick={() => {
                  setConfirming(false);
                }}
              >
                {t('settings.signOutCancel')}
              </button>
              <button
                type="button"
                className={styles.confirm}
                onClick={() => {
                  setConfirming(false);
                  void signOut();
                }}
              >
                {t('settings.signOutConfirm')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}
